import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Building2,
  Users,
  Smartphone,
  ShieldCheck,
  UserCog,
  LayoutGrid,
  Rows3,
  RefreshCw,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";

/**
 * SuperAdmin → per-obedience adoption statistics.
 *
 * Displays a compact KPI strip (totals) followed by a per-obedience view
 * the SA can toggle between visual cards and a condensed table. Mounted
 * directly into `SuperAdminPage.jsx` above the tenants table.
 *
 * Backed by `GET /api/superadmin/obedience-stats` (added 2026-05-18).
 */
export default function SuperAdminObedienceStats() {
  const { t, i18n } = useTranslation();
  const en = i18n.language !== "ro";

  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  // Default to CARDS (more visual / modern, per user choice).
  const [view, setView] = useState("cards");

  // Avoid t() warnings — we deliberately localise inline here so there's
  // no dependency on missing translation keys.
  void t;

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/superadmin/obedience-stats");
      setRows(Array.isArray(data?.obediences) ? data.obediences : []);
      setTotals(data?.totals || null);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const labels = useMemo(
    () =>
      en
        ? {
            heading: "PLATFORM ADOPTION",
            sub: "Mobile-app usage and lodges created per obedience",
            view: "View",
            cards: "Cards",
            table: "Table",
            refresh: "Refresh",
            kpis: {
              obediences: "Obediences",
              lodges: "Lodges (web)",
              members: "Members",
              mobile: "Mobile app users",
              admins: "Admins",
            },
            cols: {
              obedience: "Obedience",
              country: "Country",
              lodges: "Lodges",
              members: "Members",
              mobile: "Mobile users",
              admins: "Admins",
              app_users: "App accounts",
            },
            empty: "No obediences registered yet.",
            mobileHint:
              "Counts brothers with biometric enabled, prior mobile login, or push token registered.",
            none: "—",
          }
        : {
            heading: "ADOPȚIE PLATFORMĂ",
            sub: "Utilizare aplicație mobilă și loji create per obediență",
            view: "Vizualizare",
            cards: "Carduri",
            table: "Tabel",
            refresh: "Reîncarcă",
            kpis: {
              obediences: "Obediențe",
              lodges: "Loji (web)",
              members: "Frați înscriși",
              mobile: "Frați pe mobil",
              admins: "Admini",
            },
            cols: {
              obedience: "Obediență",
              country: "Țară",
              lodges: "Loji",
              members: "Frați",
              mobile: "Mobil",
              admins: "Admini",
              app_users: "Conturi",
            },
            empty: "Nicio obediență înregistrată încă.",
            mobileHint:
              "Contorizează frații cu biometric activat, login mobil în istoric sau token push înregistrat.",
            none: "—",
          },
    [en],
  );

  return (
    <section
      data-testid="sa-obedience-stats"
      style={{
        background: "linear-gradient(135deg,#0d1b2a 0%,#15243a 100%)",
        border: "1px solid #c9a84c",
        borderRadius: 8,
        padding: "1.4rem 1.6rem 1.6rem",
        marginBottom: "1.8rem",
        color: "#f4e9c8",
        boxShadow: "0 6px 24px rgba(13,27,42,0.18)",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: "1.1rem",
        }}
      >
        <div>
          <h2
            style={{
              fontFamily: "Cinzel, serif",
              fontSize: "1.05rem",
              letterSpacing: "0.18em",
              margin: 0,
              color: "#f4e9c8",
            }}
          >
            {labels.heading}
          </h2>
          <p
            style={{
              fontFamily: "Crimson Text, serif",
              fontSize: "0.9rem",
              color: "#c9a84c",
              margin: "0.25rem 0 0",
              fontStyle: "italic",
            }}
          >
            {labels.sub}
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              fontFamily: "Cinzel, serif",
              fontSize: "0.7rem",
              letterSpacing: "0.16em",
              color: "#c9a84c",
            }}
          >
            {labels.view}:
          </span>
          <div
            role="tablist"
            style={{
              display: "inline-flex",
              border: "1px solid #c9a84c",
              borderRadius: 999,
              overflow: "hidden",
              background: "rgba(0,0,0,0.25)",
            }}
          >
            {[
              { k: "cards", label: labels.cards, icon: <LayoutGrid size={13} /> },
              { k: "table", label: labels.table, icon: <Rows3 size={13} /> },
            ].map((tab) => {
              const isActive = view === tab.k;
              return (
                <button
                  key={tab.k}
                  data-testid={`sa-stats-view-${tab.k}`}
                  onClick={() => setView(tab.k)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "0.4rem 0.95rem",
                    fontFamily: "Cinzel, serif",
                    fontSize: "0.72rem",
                    letterSpacing: "0.14em",
                    background: isActive
                      ? "linear-gradient(135deg,#c9a84c 0%,#a78739 100%)"
                      : "transparent",
                    color: isActive ? "#0d1b2a" : "#f4e9c8",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              );
            })}
          </div>
          <button
            data-testid="sa-stats-refresh"
            onClick={load}
            title={labels.refresh}
            disabled={loading}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "0.45rem 0.85rem",
              border: "1px solid #c9a84c",
              background: "transparent",
              color: "#f4e9c8",
              cursor: loading ? "wait" : "pointer",
              borderRadius: 6,
              fontFamily: "Cinzel, serif",
              fontSize: "0.7rem",
              letterSpacing: "0.14em",
              opacity: loading ? 0.7 : 1,
            }}
          >
            <RefreshCw size={12} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
            {labels.refresh}
          </button>
        </div>
      </header>

      {/* KPI strip (totals) */}
      {totals && (
        <div
          data-testid="sa-stats-totals"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 12,
            marginBottom: "1.2rem",
          }}
        >
          <KpiTile icon={<Building2 size={16} />} label={labels.kpis.obediences} value={totals.obediences_count} />
          <KpiTile icon={<Building2 size={16} />} label={labels.kpis.lodges} value={totals.lodges_count} />
          <KpiTile icon={<Users size={16} />} label={labels.kpis.members} value={totals.members_count} />
          <KpiTile
            icon={<Smartphone size={16} />}
            label={labels.kpis.mobile}
            value={totals.mobile_users_count}
            highlight
          />
          <KpiTile icon={<ShieldCheck size={16} />} label={labels.kpis.admins} value={totals.admins_count} />
        </div>
      )}

      {err && (
        <div
          style={{
            background: "rgba(217,87,87,0.12)",
            border: "1px solid #d95757",
            color: "#ffd6d6",
            padding: "0.6rem 0.85rem",
            borderRadius: 6,
            marginBottom: "0.85rem",
            fontFamily: "Crimson Text, serif",
          }}
        >
          {err}
        </div>
      )}

      {loading && !rows.length ? (
        <p style={{ fontStyle: "italic", color: "#c9a84c", margin: 0 }}>…</p>
      ) : rows.length === 0 ? (
        <p style={{ fontStyle: "italic", color: "#c9a84c", margin: 0 }}>{labels.empty}</p>
      ) : view === "cards" ? (
        <div
          data-testid="sa-stats-cards"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 14,
          }}
        >
          {rows.map((r) => (
            <ObedienceCard key={r.id} row={r} labels={labels} />
          ))}
        </div>
      ) : (
        <div
          data-testid="sa-stats-table"
          style={{
            overflowX: "auto",
            border: "1px solid rgba(201,168,76,0.4)",
            borderRadius: 6,
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontFamily: "Crimson Text, serif",
              color: "#f4e9c8",
            }}
          >
            <thead>
              <tr
                style={{
                  background: "rgba(201,168,76,0.12)",
                  textAlign: "left",
                }}
              >
                {[
                  labels.cols.obedience,
                  labels.cols.country,
                  labels.cols.lodges,
                  labels.cols.members,
                  labels.cols.mobile,
                  labels.cols.admins,
                  labels.cols.app_users,
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: "0.6rem 0.85rem",
                      fontFamily: "Cinzel, serif",
                      fontSize: "0.7rem",
                      letterSpacing: "0.14em",
                      color: "#c9a84c",
                      borderBottom: "1px solid rgba(201,168,76,0.35)",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`sa-stats-row-${r.id}`}
                  style={{ borderBottom: "1px solid rgba(201,168,76,0.18)" }}
                >
                  <td style={{ padding: "0.55rem 0.85rem" }}>
                    <strong style={{ color: "#f4e9c8" }}>
                      {r.obedience_abbreviation || r.name}
                    </strong>
                    <div style={{ fontSize: "0.78rem", color: "#c9a84c" }}>{r.name}</div>
                  </td>
                  <td style={{ padding: "0.55rem 0.85rem" }}>
                    {r.country || labels.none}
                    {r.city ? (
                      <span style={{ color: "#9c8a55" }}> · {r.city}</span>
                    ) : null}
                  </td>
                  <td style={{ padding: "0.55rem 0.85rem", fontVariantNumeric: "tabular-nums" }}>
                    {r.lodges_count}
                  </td>
                  <td style={{ padding: "0.55rem 0.85rem", fontVariantNumeric: "tabular-nums" }}>
                    {r.members_count}
                  </td>
                  <td
                    style={{
                      padding: "0.55rem 0.85rem",
                      fontVariantNumeric: "tabular-nums",
                      color: r.mobile_users_count > 0 ? "#f4e9c8" : "#9c8a55",
                      fontWeight: r.mobile_users_count > 0 ? 600 : 400,
                    }}
                  >
                    {r.mobile_users_count}
                  </td>
                  <td style={{ padding: "0.55rem 0.85rem", fontVariantNumeric: "tabular-nums" }}>
                    {r.admins_count}
                  </td>
                  <td style={{ padding: "0.55rem 0.85rem", fontVariantNumeric: "tabular-nums" }}>
                    {r.app_users_count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p
        style={{
          marginTop: "0.85rem",
          marginBottom: 0,
          fontFamily: "Crimson Text, serif",
          fontSize: "0.78rem",
          color: "#9c8a55",
          fontStyle: "italic",
        }}
      >
        <Smartphone size={11} style={{ display: "inline", marginRight: 4, verticalAlign: "middle" }} />
        {labels.mobileHint}
      </p>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </section>
  );
}

function KpiTile({ icon, label, value, highlight }) {
  return (
    <div
      style={{
        background: highlight
          ? "linear-gradient(135deg,#c9a84c 0%,#a78739 100%)"
          : "rgba(255,255,255,0.04)",
        border: "1px solid #c9a84c",
        borderRadius: 6,
        padding: "0.7rem 0.9rem",
        display: "flex",
        flexDirection: "column",
        gap: 4,
        color: highlight ? "#0d1b2a" : "#f4e9c8",
      }}
    >
      <span
        style={{
          fontFamily: "Cinzel, serif",
          fontSize: "0.68rem",
          letterSpacing: "0.14em",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          opacity: highlight ? 0.85 : 0.78,
        }}
      >
        {icon}
        {label}
      </span>
      <span
        style={{
          fontFamily: "Cinzel, serif",
          fontSize: "1.65rem",
          fontWeight: 600,
          letterSpacing: "0.04em",
        }}
      >
        {value}
      </span>
    </div>
  );
}

function ObedienceCard({ row, labels }) {
  return (
    <article
      data-testid={`sa-stats-card-${row.id}`}
      style={{
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(201,168,76,0.45)",
        borderRadius: 6,
        padding: "1rem 1.1rem 1.05rem",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <header style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
        <div>
          <h3
            style={{
              margin: 0,
              fontFamily: "Cinzel, serif",
              fontSize: "0.95rem",
              letterSpacing: "0.12em",
              color: "#f4e9c8",
            }}
          >
            {row.obedience_abbreviation || row.name}
          </h3>
          {row.subscription ? (
            <span
              data-testid={`sa-sub-badge-${row.id}`}
              style={{
                display: "inline-block",
                marginTop: 4,
                fontSize: "0.6rem",
                fontFamily: "Cinzel, serif",
                letterSpacing: "0.12em",
                padding: "2px 8px",
                borderRadius: 4,
                border: "1px solid",
                borderColor: row.subscription.blocked ? "#d95757" : row.subscription.exempt ? "#7dbb7a" : "#c9a84c",
                color: row.subscription.blocked ? "#ffb0b0" : row.subscription.exempt ? "#b9e5b6" : "#f4e9c8",
              }}
            >
              {row.subscription.blocked
                ? "BLOCAT — NEPLATĂ"
                : row.subscription.exempt_manual
                  ? "EXONERAT DE PLATĂ"
                  : row.subscription.exempt
                    ? "ABONAMENT GRATUIT"
                    : `PLĂTIT → ${String(row.subscription.paid_until || "").slice(0, 10)} · ${row.subscription.monthly_total} €/lună`}
            </span>
          ) : null}
          <p
            style={{
              margin: "0.15rem 0 0",
              fontFamily: "Crimson Text, serif",
              fontSize: "0.85rem",
              color: "#c9a84c",
              fontStyle: "italic",
            }}
          >
            {row.name}
          </p>
          <p
            style={{
              margin: "0.1rem 0 0",
              fontFamily: "Crimson Text, serif",
              fontSize: "0.78rem",
              color: "#9c8a55",
            }}
          >
            {[row.city, row.country].filter(Boolean).join(", ") || labels.none}
          </p>
        </div>
        <span
          style={{
            background: "linear-gradient(135deg,#c9a84c 0%,#a78739 100%)",
            color: "#0d1b2a",
            fontFamily: "Cinzel, serif",
            fontSize: "0.65rem",
            letterSpacing: "0.14em",
            padding: "0.25rem 0.55rem",
            borderRadius: 4,
            whiteSpace: "nowrap",
          }}
        >
          {row.lodges_count} {row.lodges_count === 1 ? labels.cols.lodges.replace(/s$/i, "") : labels.cols.lodges}
        </span>
      </header>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 8,
        }}
      >
        <CardMetric
          icon={<Users size={13} />}
          label={labels.cols.members}
          value={row.members_count}
        />
        <CardMetric
          icon={<Smartphone size={13} />}
          label={labels.cols.mobile}
          value={row.mobile_users_count}
          accent
        />
        <CardMetric
          icon={<ShieldCheck size={13} />}
          label={labels.cols.admins}
          value={row.admins_count}
        />
        <CardMetric
          icon={<UserCog size={13} />}
          label={labels.cols.app_users}
          value={row.app_users_count}
        />
      </div>
    </article>
  );
}

function CardMetric({ icon, label, value, accent }) {
  return (
    <div
      style={{
        background: accent ? "rgba(201,168,76,0.18)" : "rgba(0,0,0,0.18)",
        border: accent ? "1px solid #c9a84c" : "1px solid rgba(201,168,76,0.25)",
        borderRadius: 4,
        padding: "0.55rem 0.7rem",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <span
        style={{
          fontFamily: "Cinzel, serif",
          fontSize: "0.62rem",
          letterSpacing: "0.14em",
          color: accent ? "#f4e9c8" : "#c9a84c",
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          opacity: 0.92,
        }}
      >
        {icon}
        {label}
      </span>
      <span
        style={{
          fontFamily: "Cinzel, serif",
          fontSize: "1.15rem",
          fontWeight: 600,
          color: "#f4e9c8",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </span>
    </div>
  );
}
